import { useAtomValue } from 'jotai';
import { Button, Menu, MenuItem, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper, HeaderActionWrapper } from '../layout/styled';
import { AssigmentUserTable } from './components/AssigmentUserTable';
import { isSmallScreenState } from '../../state/UIState';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { Typography } from '../../components/Typography/Typography';
import { useUsers } from '../../queries/hooks/useUsers';
import { isSubmitter } from '../../utils/roles';
import { exportRowsToCsv, exportRowsToXlsx } from '../../utils/tableExport';

const EXPORT_HEADERS = ['Submitter', 'Deals'];
const EXPORT_FILENAME = 'Timesheet_Assignments';
const EXPORT_SHEET_NAME = 'Timesheet Assignments';

const HeaderAction = () => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { data: users } = useUsers();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const rows = useMemo(() => {
    const ops = (users ?? []).filter((user) => isSubmitter(user));
    return ops.map((op) => [
      op.name ?? '',
      (op.operatingPartnerDeals ?? []).map((d) => d.name).join(', '),
    ]);
  }, [users]);

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportXlsx = () => {
    exportRowsToXlsx({
      headers: EXPORT_HEADERS,
      rows,
      filename: EXPORT_FILENAME,
      sheetName: EXPORT_SHEET_NAME,
    });
    handleExportClose();
  };

  const handleExportCsv = () => {
    exportRowsToCsv({
      headers: EXPORT_HEADERS,
      rows,
      filename: EXPORT_FILENAME,
    });
    handleExportClose();
  };

  return (
    <HeaderActionWrapper>
      <Button
        variant='outlined'
        startIcon={<DownloadIcon />}
        style={{
          backgroundColor: colors.surfaceBackground.bg1,
          marginBottom: isSmallScreen ? '10px' : '0px',
          height: '40px',
        }}
        onClick={handleExportClick}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportXlsx}>
          <Typography
            variant='body'
            color={colors.text.main}
            style={{ marginLeft: '4px', marginBottom: '-2px' }}
          >
            Export as XLSX
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleExportCsv}>
          <Typography
            variant='body'
            color={colors.text.main}
            style={{ marginLeft: '4px', marginBottom: '-2px' }}
          >
            Export as CSV
          </Typography>
        </MenuItem>
      </Menu>
    </HeaderActionWrapper>
  );
};

export const TimesheetAssignment = () => {
  const isSmallScreen = useAtomValue(isSmallScreenState);

  return (
    <>
      <PageHeader
        title='Timesheet Assignment'
        description='Assign the deals each submitter can log against'
        breadcrumbs={[{ title: 'Administration' }, { title: 'Timesheet Assignment' }]}
        actionComponent={<HeaderAction />}
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <AssigmentUserTable />
      </ContentWrapper>
    </>
  );
};
